import { defaultIdFactory, type IdFactory } from "@/shell/layout";

import { PRESET_LAYOUTS } from "./presets";
import type { Workspace } from "./types";

interface ActionOptions {
  /** Override la generation d'IDs (utilise pour les tests). */
  idFactory?: IdFactory;
  /** Override le timestamp now (utilise pour les tests). */
  now?: () => number;
}

function resolveIdFactory(opts?: ActionOptions): IdFactory {
  return opts?.idFactory ?? defaultIdFactory;
}

function resolveNow(opts?: ActionOptions): number {
  return opts?.now ? opts.now() : Date.now();
}

/* ============================================================ */
/* Pure action functions — toutes operent sur le map workspaces  */
/* et retournent un nouveau map (immuable).                       */
/* ============================================================ */

export interface SaveAsNewResult {
  workspaces: Record<string, Workspace>;
  newId: string;
}

/**
 * Cree un nouveau workspace custom a partir du layout du workspace courant.
 * Le nouveau workspace est isPreset = false (deletable, pas resetable).
 */
export function actionSaveCurrentAsNew(
  workspaces: Record<string, Workspace>,
  currentId: string,
  newName: string,
  opts?: ActionOptions
): SaveAsNewResult {
  const current = workspaces[currentId];
  if (!current) return { workspaces, newId: currentId };
  const newId = resolveIdFactory(opts)();
  const now = resolveNow(opts);
  const newWorkspace: Workspace = {
    id: newId,
    name: newName,
    layout: current.layout,
    isPreset: false,
    createdAt: now,
    updatedAt: now,
  };
  return {
    workspaces: { ...workspaces, [newId]: newWorkspace },
    newId,
  };
}

/** Renomme un workspace. Preset ou custom — peu importe. */
export function actionRename(
  workspaces: Record<string, Workspace>,
  id: string,
  newName: string,
  opts?: ActionOptions
): Record<string, Workspace> {
  const ws = workspaces[id];
  if (!ws || ws.name === newName) return workspaces;
  return {
    ...workspaces,
    [id]: { ...ws, name: newName, updatedAt: resolveNow(opts) },
  };
}

/** Supprime un workspace. No-op si c'est un preset (les presets ne se suppriment pas). */
export function actionDelete(
  workspaces: Record<string, Workspace>,
  id: string
): Record<string, Workspace> {
  const ws = workspaces[id];
  if (!ws || ws.isPreset) return workspaces;
  const next = { ...workspaces };
  delete next[id];
  return next;
}

/**
 * Restaure un preset a son etat d'origine (rebuild via PRESET_LAYOUTS).
 * No-op si le workspace n'est pas un preset ou n'a pas de builder.
 */
export function actionResetToPreset(
  workspaces: Record<string, Workspace>,
  id: string,
  opts?: ActionOptions
): Record<string, Workspace> {
  const ws = workspaces[id];
  if (!ws || !ws.isPreset) return workspaces;
  const builder = PRESET_LAYOUTS[id];
  if (!builder) return workspaces;
  return {
    ...workspaces,
    [id]: { ...ws, layout: builder(), updatedAt: resolveNow(opts) },
  };
}

/** Duplique un workspace — copie le layout, nouveau ID, isPreset=false. */
export function actionDuplicate(
  workspaces: Record<string, Workspace>,
  id: string,
  newName: string,
  opts?: ActionOptions
): SaveAsNewResult {
  const ws = workspaces[id];
  if (!ws) return { workspaces, newId: id };
  const newId = resolveIdFactory(opts)();
  const now = resolveNow(opts);
  return {
    workspaces: {
      ...workspaces,
      [newId]: {
        id: newId,
        name: newName,
        layout: ws.layout,
        isPreset: false,
        createdAt: now,
        updatedAt: now,
      },
    },
    newId,
  };
}
